import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import {
  Company,
  ContainerTypes,
  Country,
  CrmTag,
  Currency,
  CurrencyExchange,
  DesconsolidationType,
  DocumentType,
  Equipment,
  EquipmentType,
  EventType,
  FeeType,
  Flow,
  FlowService,
  GetParams,
  Good,
  LoadUnit,
  Location,
  LocationType,
  Motonave,
  PackageType,
  Partner,
  PartnerType,
  Product,
  ServiceType,
  Shipping,
  Stages,
  State,
  TransportType,
  UnitOfMeasure,
  UnitOfMeasureCategory,
  User,
  UserRole,
  CompanyTaxes,
} from '@app/shared';
import {
  ActivityService,
  CompanyService,
  CurrencyService,
  DeconsolidationTypeService,
  DocumentService,
  EquipmentService,
  FeeService,
  FlowEventService,
  GoodService,
  LoadUnitService,
  LocationTransportService,
  OpportunityService,
  PartnerService,
  StateService,
  StServiceService,
  UserService,
} from '@app/core';
import { MotonaveService } from './motonave.service';
import { ShippingService } from './shipping.service';
import { StagesService } from './stages.service';
import { PRODUCT_GLOBAL_INCLUDES, ProductsService } from './products.service';
import { UnitOfMeasureService } from './unit-of-measure.service';
import { UnitOfMeasureCategoryService } from './unit-of-measure-category.service';
import { CompanyTaxesService } from './company-taxes.service';
import { CostCenter } from '../../shared/models/cost-center';
import { SalesTeam } from '../../shared/models/sales-team';
import { LossReason } from '../../shared/models/loss-reason';
import { ActivityType } from '../../shared/models/activity-type';

@Injectable({
  providedIn: 'root',
})
export class GlobalValuesService {
  currencies$ = new BehaviorSubject<Currency[]>([]);
  currenciesExchange$ = new BehaviorSubject<CurrencyExchange[]>([]);
  flows$ = new BehaviorSubject<Flow[]>([]);
  places$ = new BehaviorSubject<Location[]>([]);
  transportTypes$ = new BehaviorSubject<TransportType[]>([]);
  users$ = new BehaviorSubject<User[]>([]);
  userRoles$ = new BehaviorSubject<UserRole[]>([]);
  locationsTypes$ = new BehaviorSubject<LocationType[]>([]);
  companies$ = new BehaviorSubject<Company[]>([]);
  documentTypes$ = new BehaviorSubject<DocumentType[]>([]);
  countries$ = new BehaviorSubject<Country[]>([]);
  feeTypes$ = new BehaviorSubject<FeeType[]>([]);
  equipmentTypes$ = new BehaviorSubject<EquipmentType[]>([]);
  states$ = new BehaviorSubject<State[]>([]);
  eventTypes$ = new BehaviorSubject<EventType[]>([]);
  partnerTypes$ = new BehaviorSubject<PartnerType[]>([]);
  motonaves$ = new BehaviorSubject<Motonave[]>([]);
  packageTypes$ = new BehaviorSubject<PackageType[]>([]);
  goods$ = new BehaviorSubject<Good[]>([]);
  serviceTypes$ = new BehaviorSubject<ServiceType[]>([]);
  shipping$ = new BehaviorSubject<Shipping[]>([]);
  equipments$ = new BehaviorSubject<Equipment[]>([]);
  costCenters$ = new BehaviorSubject<CostCenter[]>([]);
  crmTags$ = new BehaviorSubject<CrmTag[]>([]);
  stages$ = new BehaviorSubject<Stages[]>([]);
  companyTaxes$ = new BehaviorSubject<CompanyTaxes[]>([]);
  salesTeams$ = new BehaviorSubject<SalesTeam[]>([]);
  lossReasons$ = new BehaviorSubject<LossReason[]>([]);
  activityTypes$ = new BehaviorSubject<ActivityType[]>([]);
  products$ = new BehaviorSubject<Product[]>([]);
  unitOfMeasures$ = new BehaviorSubject<UnitOfMeasure[]>([]);
  unitOfMeasureCategories$ = new BehaviorSubject<UnitOfMeasureCategory[]>([]);

  isPartnerReady$ = new BehaviorSubject<boolean>(false);
  isStagesReady$ = new BehaviorSubject<boolean>(false);
  isUnitOfMeasureReady$ = new BehaviorSubject<boolean>(false);
  isCompanyTaxesReady$ = new BehaviorSubject<boolean>(false);
  stageslist: Stages[] = [];
  unitOfMeasureslist: UnitOfMeasure[] = [];
  companyTaxeslist: CompanyTaxes[] = [];
  partnerlist: Partner[] = [];
  mojitoEmployeeList: Partner[] = [];
  cecEmployeeList: Partner[] = [];
  clientsList: Partner[] = [];
  driversList: Partner[] = [];
  forwarderList: Partner[] = [];
  providerList: Partner[] = [];
  clientAndProviderList: Partner[] = [];
  supplierList: Partner[] = [];
  origenContactList: Partner[] = [];
  destinyContactList: Partner[] = [];
  shipperList: Partner[] = [];
  consigneeList: Partner[] = [];

  deconsolidationTypes: DesconsolidationType[] = [];
  loadUnits: LoadUnit[] = [];

  containerTypes$ = new BehaviorSubject<ContainerTypes[]>([]);

  _isFlowDataReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  get isFlowDataReady(): Observable<boolean> {
    return this._isFlowDataReady as Observable<boolean>;
  }

  constructor(
    private currencyService: CurrencyService,
    private flowEventService: FlowEventService,
    private locationTransportService: LocationTransportService,
    private userService: UserService,
    private companyService: CompanyService,
    private documentService: DocumentService,
    private feeService: FeeService,
    private equipmentService: EquipmentService,
    private stateService: StateService,
    private partnerService: PartnerService,
    private deconsolidationTypeService: DeconsolidationTypeService,
    private loadUnitService: LoadUnitService,
    private stServiceService: StServiceService,
    private motonaveService: MotonaveService,
    private goodService: GoodService,
    private shippingService: ShippingService,
    private opportunityService: OpportunityService,
    private stagesService: StagesService,
    private productsService: ProductsService,
    private unitOfMeasureService: UnitOfMeasureService,
    private unitOfMeasureCategoryService: UnitOfMeasureCategoryService,
    private companyTaxesService: CompanyTaxesService,
    private activityService: ActivityService
  ) {
    const params: GetParams = {
      startRow: 0,
      endRow: 5000,
      sortDirection: 'asc',
      sortProperty: 'id',
    };
    const flowParams: GetParams = {
      ...params,
      filterProperty: 'isNative',
      filterValue: 'true',
    };

    const request = {
      currencies: currencyService.getCurrency(params),
      currenciesExchange: currencyService.getCurrencyExchange(params),
      flows: flowEventService.getFlow(flowParams),
      places: locationTransportService.getLocation(params),
      transportTypes: locationTransportService.getTransportType(params),
      users: userService.getUsers(params),
      userRoles: userService.getUserRoles(params),
      locationsTypes: locationTransportService.getLocationType(params),
      companies: companyService.getCompany(params),
      documentTypes: documentService.getDocumentType(params),
      countries: locationTransportService.getCountry(params),
      feeTypes: feeService.getFeeType(params),
      equipmentTypes: equipmentService.getEquipmentType(params),
      states: stateService.getState(params),
      eventTypes: flowEventService.getEventType(params),
      partners: partnerService.getPartner(params),
      partnerType: partnerService.getPartnerType(params),
      deconsolidationTypes:
        deconsolidationTypeService.getDesconsolidationType(params),
      loadUnitService: loadUnitService.getLoadUnit(params),
      equipments: equipmentService.getEquipment(params),
      containerTypes: stServiceService.getContainerTypes(),
      serviceTypes: stServiceService.getServiceType(params),
      motonaves: motonaveService.getMotonave(params),
      packageTypes: loadUnitService.getPackageType(params),
      goods: goodService.getGood(params),
      shipping: shippingService.getShipping(params),
      costCenters: opportunityService.getCostCenter(params),
      crmTags: opportunityService.getCrmTag(params),
      stages: this.stagesService.getStages(params),
      unitOfMeasures: this.unitOfMeasureService.getUnitOfMeasure(params),
      unitOfMeasureCategories:
        this.unitOfMeasureCategoryService.getUnitOfMeasureCategory(params),
      companyTaxes: this.companyTaxesService.getCompanyTaxes(params),
      salesTeams: opportunityService.getSalesTeam(params),
      lossReason: opportunityService.getLossReason(params),
      activitiesType: activityService.getActivityTypes(params),
      products: productsService.getAdvancedProducts({
        ...params,
        includes: [...PRODUCT_GLOBAL_INCLUDES],
      }),
    };

    forkJoin(request).subscribe({
      next: ({
        currencies,
        currenciesExchange,
        flows,
        places,
        transportTypes,
        users,
        userRoles,
        locationsTypes,
        companies,
        documentTypes,
        countries,
        feeTypes,
        equipmentTypes,
        states,
        eventTypes,
        partners,
        partnerType,
        deconsolidationTypes,
        loadUnitService,
        equipments,
        containerTypes,
        serviceTypes,
        motonaves,
        packageTypes,
        goods,
        shipping,
        costCenters,
        crmTags,
        stages,
        companyTaxes,
        salesTeams,
        lossReason,
        activitiesType,
        products,
        unitOfMeasures,
        unitOfMeasureCategories,
      }) => {
        this.currencies$.next(currencies.data.rows);
        this.currenciesExchange$.next(currenciesExchange.data.rows);
        this.flows$.next(flows.data.rows);
        this.places$.next(places.data.rows);
        this.transportTypes$.next(transportTypes.data.rows);
        this.users$.next(users.data.rows);
        this.userRoles$.next(userRoles.data.rows);
        this.locationsTypes$.next(locationsTypes.data.rows);
        this.companies$.next(companies.data.rows);
        this.documentTypes$.next(documentTypes.data.rows);
        this.countries$.next(countries.data.rows);
        this.feeTypes$.next(feeTypes.data.rows);
        this.equipmentTypes$.next(equipmentTypes.data.rows);
        this.states$.next(states.data.rows);
        this.eventTypes$.next(eventTypes.data.rows);
        this.partnerTypes$.next(partnerType.data.rows);
        this.equipments$.next(equipments.data.rows);
        this.containerTypes$.next(containerTypes.data.rows);
        this.serviceTypes$.next(serviceTypes.data.rows);
        this.motonaves$.next(motonaves.data.rows);
        this.packageTypes$.next(packageTypes.data.rows);
        this.goods$.next(goods.data.rows);
        this.shipping$.next(shipping.data.rows);
        this.costCenters$.next(costCenters.data.rows);
        this.crmTags$.next(crmTags.data.rows);
        this.stages = stages.data.rows;
        this.unitOfMeasures = unitOfMeasures.data.rows;
        this.companyTaxes = companyTaxes.data.rows; // Usa el setter
        this.salesTeams$.next(salesTeams.data.rows);
        this.lossReasons$.next(lossReason.data.rows);
        this.activityTypes$.next(activitiesType.data.rows);
        this.products$.next(products.data.rows);
        this.unitOfMeasures$.next(unitOfMeasures.data.rows);
        this.unitOfMeasureCategories$.next(unitOfMeasureCategories.data.rows);

        this.partner = partners.data.rows;

        this.setEventFlow(this.flows$.value || []);

        this.deconsolidationTypes = deconsolidationTypes.data.rows;
        //this.loadUnits = loadUnitService.data.rows;
      },
      error: (e) => console.error(e),
    });
    //getUserRoles don't return a httpRespond
    // this.userService.getUserRoles(params).subscribe({
    //   next: (roles) => this.userRoles$.next(roles),
    //   error: (e) => console.error(e),
    // })
  }

  setEventFlow(flows: Flow[]) {
    flows.forEach((flow) => {
      (flow.events || []).forEach((event) => {
        // ✅ Protección: Si el evento ya tiene activator configurado, saltarlo para evitar duplicación
        // Esto previene que eventos ya procesados se procesen nuevamente si setEventFlow se llama múltiples veces
        if (event.activator && event.activator.name) {
          return; // Ya procesado, continuar con el siguiente
        }
        
        // Configuracion de los estados especiales
        if (event.name === 'Tarea creada') {
          event.activator = {
            name: 'Automático',
            value: undefined,
            location: event.location || undefined,
          };
          event.icon = 'playlist_add_check';
          event.color = '#FF990A';
          return;
        }

        const dynamicField =
          this.flowEventService.mojitoDynamicFieldOptions.find(
            (i) => i.enum === event.mojitoDynamicField
          );

        if (event.name === 'Tarea finalizada') {
          event.activator = {
            name: 'Manual',
            value: undefined,
            location: event.location || undefined,
            mojitoDynamicField: dynamicField,
          };
          event.icon = 'check_circle';
          event.color = '#45A557';
          return;
        }

        if (event.eventTriggers && event.eventTriggers.length > 0) {
          const trigger = event.eventTriggers[0];
          const typeId = trigger?.eventTriggerType?.id ?? trigger?.eventTriggerTypeId;
          let name:
            | 'Subida documento'
            | 'Fecha Mojito'
            | 'Validacion documento'
            | 'Manual' = 'Manual';
          let value: any;
          const location: Location | undefined = event.location;

          if (typeId === 1) {
            name = 'Subida documento';
            value = trigger.documentType?.id ?? trigger.documentTypeId ?? '-';
          } else if (typeId === 3) {
            name = 'Fecha Mojito';
            value = trigger.integrationField;
          } else if (typeId === 4) {
            name = 'Validacion documento';
            value = trigger.documentType?.id ?? trigger.documentTypeId ?? '-';
          }

          event.activator = {
            name,
            value,
            location,
            mojitoDynamicField:
              this.flowEventService.mojitoDynamicFieldOptions.find(
                (i) => i.enum === event.mojitoDynamicField
              ),
          };
        } else {
          event.activator = {
            name: 'Manual',
            value: undefined,
            location: event.location || undefined,
            mojitoDynamicField: dynamicField,
          };
        }
      });

      this._isFlowDataReady.next(true);
    });
  }

  set partner(list: Partner[]) {
    this.isPartnerReady$.next(false);

    this.partnerlist = list.sort((a, b) => {
      // Primero por nombre
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;

      // Si los nombres son iguales, ordenar por apellido
      return (a.surName ?? '').localeCompare(b.surName);
    });

    this.refreshDerivedPartnerLists();

    this.isPartnerReady$.next(true);
  }

  /**
   * Quita un contacto del estado global sin reconstruir la lista completa.
   *
   * El setter `partner` esta pensado para una carga completa: reordena
   * alfabeticamente todo lo que reciba. Usarlo para borrar obliga al llamante a
   * pasarle su propia lista, que puede ser un subconjunto (por ejemplo el
   * resultado de una agrupacion), y entonces ese subconjunto se guarda como si
   * fuera el censo completo y mutila `partnerlist` y sus listas derivadas para
   * toda la aplicacion. Este metodo borra por id sobre el estado real y conserva
   * el orden vigente.
   */
  removePartner(id: number): void {
    this.isPartnerReady$.next(false);

    this.partnerlist = this.partnerlist.filter((p) => p.id !== id);
    this.refreshDerivedPartnerLists();

    this.isPartnerReady$.next(true);
  }

  /**
   * Reasigna la empresa (`parentId`) de varios contactos en el estado global.
   *
   * Mismo motivo que `removePartner`: el setter `partner` esta pensado para una
   * carga completa y reordena todo lo que reciba, asi que usarlo para tocar unos
   * pocos contactos obliga al llamante a pasar su propia lista y arriesga mutilar
   * el censo. Aqui se actualiza por id sobre el estado real y se conserva el orden.
   *
   * Hace falta porque asociar empleados a una empresa escribe en el contacto HIJO:
   * sin esto el listado de Contactos seguiria mostrando la empresa anterior hasta
   * recargar la aplicacion.
   */
  updatePartnersParent(
    partnerIds: number[],
    parent: { id: number; name: string } | null
  ): void {
    if (!partnerIds.length) return;

    const ids = new Set(partnerIds);
    this.isPartnerReady$.next(false);

    this.partnerlist = this.partnerlist.map((p) =>
      ids.has(p.id)
        ? {
            ...p,
            parentId: parent?.id,
            parent: parent ? { id: parent.id, name: parent.name } : undefined,
          }
        : p
    );
    this.refreshDerivedPartnerLists();

    this.isPartnerReady$.next(true);
  }

  /**
   * Recalcula las listas derivadas de `partnerlist`. Unico punto donde se define
   * que entra en cada una, compartido por la carga completa y por el borrado.
   */
  private refreshDerivedPartnerLists(): void {
    this.mojitoEmployeeList = this.partnerlist.filter(
      (p) => p.partnerTypeId === 1
    );
    this.cecEmployeeList = this.partnerlist.filter(
      (p) => p.partnerTypeId === 2
    );
    this.clientsList = this.partnerlist.filter((p) => p.partnerTypeId === 3);
    this.driversList = this.partnerlist.filter((p) => p.partnerTypeId === 4);
    this.forwarderList = this.partnerlist.filter((p) => p.partnerTypeId === 5);
    this.providerList = this.partnerlist.filter(
      (p) => p.partnerTypeId === 6 || p.partnerTypeId === 7
    );
    this.clientAndProviderList = this.partnerlist.filter(
      (p) => p.partnerTypeId === 7
    );
    this.origenContactList = this.partnerlist.filter(
      (p) => p.isCompany === false
    );
    this.destinyContactList = this.partnerlist.filter(
      (p) => p.isCompany === false
    );
    this.shipperList = this.partnerlist.filter(
      (p) =>
        (p.partnerTypeId === 3 || p.partnerTypeId === 7) && p.isCompany === true
    );
    this.consigneeList = this.partnerlist.filter(
      (p) => p.partnerTypeId === 3 || p.partnerTypeId === 7
    );
  }

  set stages(list: Stages[]) {
    this.isStagesReady$.next(false);
    this.stageslist = list;
    this.stages$.next(list);
    this.isStagesReady$.next(true);
  }
  set unitOfMeasures(list: UnitOfMeasure[]) {
    this.isUnitOfMeasureReady$.next(false);
    this.unitOfMeasureslist = list;
    this.unitOfMeasures$.next(list);
    this.isUnitOfMeasureReady$.next(true);
  }

  set companyTaxes(list: CompanyTaxes[]) {
    this.isCompanyTaxesReady$.next(false);
    this.companyTaxeslist = list;
    this.companyTaxes$.next(list);
    this.isCompanyTaxesReady$.next(true);
  }

  getServiceType(id: number): ServiceType | undefined {
    if (this.serviceTypes$.value && this.serviceTypes$.value.length > 0) {
      return this.serviceTypes$.value.find((st) => st.id === id);
    } else return undefined;
  }
}
